import banner from "../../../courses/module-2/img8.png";

const modulo8 = {
  courseId: "cripto-blockchain",
  id: "modulo-8",
  title: "🧩 MODULE 8: Glossary and Essential Mini-Concepts",
  estimatedDuration: "30 minutes",
  format: "Reading + glossary + review activity",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objective",
      content: [
        "This module is designed to help you review the essential concepts we covered throughout the course. Here you will find definitions, real examples, mini reminders, and useful links so you can clear up any doubts and prepare confidently for the final evaluation.",
      ],
    },
    {
      id: "glosario",
      navLabel: "Glossary",
      title: "🧠 Expanded glossary – General review",
      content: [
        {
          type: "paragraph",
          text: "🔗 Blockchain",
        },
        {
          type: "list",
          items: [
            "A public, digital, and decentralized record where all transactions are stored.",
            "Each block contains information and is connected to the previous one, forming a “chain”.",
            "Example: Bitcoin and Ethereum use blockchain to record who owns which cryptocurrency.",
          ],
        },
        {
          type: "paragraph",
          text: "💸 Cryptocurrencies",
        },
        {
          type: "list",
          items: [
            "Digital currencies that work without banks or governments.",
            "Examples: BTC (Bitcoin), ETH (Ethereum), SOL (Solana), ADA (Cardano), XLM (Stellar).",
            "They are used to pay, invest, or access digital services.",
          ],
        },
        {
          type: "paragraph",
          text: "🟡 Stablecoins",
        },
        {
          type: "list",
          items: [
            "Cryptocurrencies whose value is linked to a stable asset, such as the dollar.",
            "Examples: USDT, USDC, DAI.",
            "They are used to avoid the volatility of other cryptocurrencies.",
          ],
        },
        {
          type: "paragraph",
          text: "🌐 Web3",
        },
        {
          type: "list",
          items: [
            "The new version of the Internet that puts control in the hands of users.",
            "It is based on blockchain, decentralization, and digital ownership.",
            "Differences:",
          ],
        },
        {
          type: "orderedList",
          items: [
            "Web 1.0: read only",
            "Web 2.0: read and write",
            "Web 3.0: read, write, and own",
          ],
        },
        {
          type: "paragraph",
          text: "🔐 Wallets",
        },
        {
          type: "list",
          items: [
            "Applications used to store and use cryptocurrencies.",
            "Types:",
          ],
        },
        {
          type: "orderedList",
          items: [
            "Custodial: the platform stores your keys, for example Binance",
            "Non-custodial: you have full control, for example Vesseo or MetaMask",
          ],
        },
        {
          type: "list",
          items: ["They are always backed up by a security seed phrase."],
        },
        {
          type: "paragraph",
          text: "🔏 Seed Phrase",
        },
        {
          type: "list",
          items: [
            "It is your master key: a list of 12 or 24 words that allows you to recover your wallet if you lose your device.",
            "Example: rain – tomato – train – empty – leaf…",
            "Never share it or store it online!",
          ],
        },
        {
          type: "paragraph",
          text: "🔍 Blockchain explorers",
        },
        {
          type: "list",
          items: [
            "Websites where you can view public information about transactions, blocks, and wallets.",
            "Examples:",
          ],
        },
        {
          type: "orderedList",
          items: ["Etherscan.io", "Solscan.io", "Stellar.expert"],
        },
        {
          type: "paragraph",
          text: "🛠️ Smart Contracts",
        },
        {
          type: "list",
          items: [
            "Programs that run automatically on the blockchain when certain conditions are met.",
            "Example: a loan contract that automatically pays itself if you deposit a certain amount of crypto.",
          ],
        },
        {
          type: "paragraph",
          text: "⚙️ Gas fees",
        },
        {
          type: "list",
          items: [
            "Fees paid to process a transaction on the blockchain.",
            "Some networks have high fees, such as Ethereum, while others are cheaper, such as Stellar or Polygon.",
          ],
        },
        {
          type: "paragraph",
          text: "👤 KYC (Know Your Customer)",
        },
        {
          type: "list",
          items: [
            "A mandatory process on many platforms to verify your identity.",
            "It helps prevent fraud, but it also means sharing your personal data.",
          ],
        },
        {
          type: "paragraph",
          text: "🔥 Key terms in Web3",
        },
        {
          type: "table",
          headers: ["Term", "Brief definition"],
          rows: [
            ["DeFi", "Decentralized finance without intermediaries"],
            [
              "NFT",
              "Non-fungible token: digital art, tickets, unique blockchain objects",
            ],
            [
              "DAO",
              "Autonomous and decentralized organization governed by its members",
            ],
            [
              "Staking",
              "“Locking” your crypto to help a network and earn rewards",
            ],
            [
              "Airdrop",
              "A free reward received for participating in or registering for a project",
            ],
            [
              "Farming",
              "Providing liquidity to a DeFi protocol in exchange for interest",
            ],
            [
              "Token",
              "Digital representation of an asset or utility. It can exist on several networks",
            ],
          ],
        },
      ],
    },
    {
      id: "prevencion",
      navLabel: "How to avoid scams",
      title: "⚠️ How to avoid scams",
      content: [
        {
          type: "list",
          items: [
            "Always verify the URL of the websites where you connect your wallet.",
            "Never share your seed phrase or private keys.",
            "Be suspicious of promises of easy or urgent money.",
            "Use trusted platforms and check their history.",
          ],
        },
      ],
    },
    {
      id: "extra",
      navLabel: "Extra note",
      title: "📌 Extra note: Do not confuse network, token, and cryptocurrency",
      content: [
        {
          type: "paragraph",
          text: "A cryptocurrency can exist on more than one network.",
        },
        {
          type: "paragraph",
          text: "Example: USDC can be on Ethereum, Polygon, Solana, and Stellar.",
        },
        {
          type: "paragraph",
          text: "If you send funds through the wrong network, you can lose them.",
        },
        {
          type: "paragraph",
          text: "✅ Before sending:",
        },
        {
          type: "list",
          items: [
            "Verify the correct network, such as ERC-20, SPL, etc.",
            "Make sure the receiver accepts that network.",
            "Confirm that the token is compatible.",
          ],
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Useful resources",
      title: "📚 Useful resources",
      content: [
        {
          type: "link",
          href: "https://academy.binance.com/es/glossary",
          text: "Binance Academy Glossary",
        },
        {
          type: "link",
          href: "https://blog.telluscoop.com/p/stellarquest1",
          text: "Stellar Quest – Learn and earn XLM",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Final activity",
      title: "✍ Final module activity",
      content: [
        "Create a summary in your notebook with your 10 favorite concepts from the whole course.",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "“Mastering the basics is what will allow you to go far in the crypto world.”",
      ],
    },
  ],
};

export default modulo8;
